// frontend/src/pages/storeActivity/billingStateRow.js
//
// How the Store Activity billing panel reads a subscription state.
//
// Separated from the panels for the reason timelineDetail.js is:
// this is the part with rules rather than markup. The rule here is that a
// state name is not an explanation. `blocked` and `payment_on_hold` are the
// CAUSES of a denial, not peers of `active` — a panel that prints the enum
// value tells a staff member what the column holds, never what happened to
// the merchant, which is the only question the page is open for.
//
// So every code gets three readings, and each is a separate lookup because
// each answers a different question:
//
//   label   — what the state is called          ("Blocked")
//   meaning — what it does to the merchant      ("Access is denied because…")
//   reason  — what the writer recorded          ("The merchant let a plan
//                                                 change lapse")
//
// The vocabularies are the backend's, verbatim: states come from
// `billing.domain.states.SubscriptionState` plus `billing.services.access.
// EVALUATION_STATUSES`; triggers from `BillingEvent.Trigger`; reasons from
// the `detail['reason']` strings the reconciler, the daily check and the
// proposal service write. The first two are pinned against the locale files
// by backend/billing/tests/test_i18n_dynamic_key_contracts.py.
//
// Where a lookup falls back is therefore about HISTORY, not about missing
// copy: `states.*` and `triggers.*` are read off BillingEvent rows that
// outlive the vocabularies that wrote them, so a retired code renders as
// itself. `meanings.*` is only ever read off the CURRENT status, which the
// pin guarantees a sentence for.

/**
 * What the state is called — or the raw code, for a state retired since the
 * BillingEvent naming it was written. Unlabelled is a prompt to add the key;
 * a blank would read as "nothing happened".
 */
export function stateLabel(state, t) {
  return t(`storeActivity.billing.states.${state}`, { defaultValue: state });
}

/**
 * What the state does to the merchant, as a sentence.
 *
 * '' only if the pin above has been broken, in which case the row disappears
 * rather than printing an enum value in a slot the reader expects prose in —
 * the label row already names it.
 */
export function stateMeaning(state, t) {
  return t(`storeActivity.billing.meanings.${state}`, { defaultValue: '' });
}

/**
 * The reason the writer recorded, or null.
 *
 * Null is the common case and a true one: only the policy writes name a
 * reason. A plain mirror sync records which fields moved and nothing else,
 * so there is no sentence to show and the row is not rendered.
 */
export function reasonLabel(reason, t) {
  if (!reason) return null;
  return t(`storeActivity.billing.reasons.${reason}`, { defaultValue: reason });
}

/** What set the transition off — a webhook, the nightly check, an install. */
export function triggerLabel(trigger, t) {
  return t(`storeActivity.billing.triggers.${trigger}`, { defaultValue: trigger });
}

/**
 * "24 Aug 2026, 15:04 · Install" — when the store entered the state it is
 * in, and what put it there.
 *
 * `null` for `since` is not a lookup that failed; the reasons a transition
 * has no audit row are in `_billing_state_since` in
 * backend/api/views/analytics.py, which is where they can be checked against
 * the writers. Saying the date is not recorded is the honest reading of all
 * of them.
 */
export function stateSinceLabel(since, t, formatDateTime) {
  if (!since) return t('storeActivity.billing.sinceUnrecorded');
  return `${formatDateTime(since.occurred_at)} · ${triggerLabel(since.trigger, t)}`;
}

/** "Free days → Active" — one row of the trail, in words. */
export function transitionLabel(event, t) {
  return t('storeActivity.billing.transition', {
    from: stateLabel(event.from_state, t),
    to: stateLabel(event.to_state, t),
  });
}
